import { type ChangeEvent, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { AttachmentRef, Comment } from '../api/types.js';
import { apiFetch } from '../api/client.js';
import { Alert, Button } from './ui.js';

const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;

/** Reads a file as base64 (without the data-URL prefix). */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(',')[1] ?? '');
    reader.onerror = () => reject(new Error('read failed'));
    reader.readAsDataURL(file);
  });
}

/** Fetches an attachment (base64) and saves it as a file in the browser. */
async function downloadAttachment(projectId: string, att: AttachmentRef): Promise<void> {
  const { attachment } = await apiFetch<{
    attachment: { filename: string; contentType: string; data: string };
  }>(`/projects/${projectId}/attachments/${att.id}`);
  const bytes = Uint8Array.from(atob(attachment.data), (c) => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: attachment.contentType }));
  const a = document.createElement('a');
  a.href = url;
  a.download = attachment.filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * A message list plus a compose box, shared by the per-story comment threads,
 * the specification discussion, and the project messaging feed. Staff can mark a
 * message internal (developer-only); a founder never sees that control or those
 * messages. A single file can be attached to any message.
 */
export function CommentThread({
  projectId,
  comments,
  isLoading,
  isError,
  onSubmit,
  isSubmitting,
  canMarkInternal,
  showSubject = false,
  newestFirst = false,
  newSince,
  currentUserId,
  storyHref,
  emptyText = 'No messages yet.',
  placeholder = 'Write a message…',
}: {
  projectId: string;
  comments: Comment[] | undefined;
  isLoading: boolean;
  isError: boolean;
  onSubmit: (input: {
    body: string;
    internal: boolean;
    attachment?: { filename: string; contentType: string; dataBase64: string };
  }) => void;
  isSubmitting: boolean;
  canMarkInternal: boolean;
  showSubject?: boolean;
  /** Show the most recent message first (used by the project message feed). */
  newestFirst?: boolean;
  /**
   * When set (alongside `currentUserId`), messages from other people newer than
   * this instant are flagged "New". `null` means the viewer has never read the
   * feed, so everything from others is new.
   */
  newSince?: string | null;
  /** The viewer, so their own messages are never flagged as new to them. */
  currentUserId?: string;
  /**
   * Builds a link target for a story comment's subject chip, so a message about
   * a story can jump straight to it. When omitted, the chip is plain text.
   */
  storyHref?: (storyId: string) => string;
  emptyText?: string;
  placeholder?: string;
}) {
  const [body, setBody] = useState('');
  const [internal, setInternal] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const onFile = (e: ChangeEvent<HTMLInputElement>): void => {
    const f = e.target.files?.[0] ?? null;
    if (f && f.size > MAX_ATTACHMENT_BYTES) {
      setError('That file is larger than 5 MB.');
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    setError(null);
    setFile(f);
  };

  const clearFile = (): void => {
    setFile(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  // The feed arrives oldest-first; the project message feed shows newest-first.
  const ordered = newestFirst && comments ? [...comments].reverse() : comments;

  // A message is "new" to this viewer when they didn't write it and it landed
  // after their last read of the feed. Only active when the caller opts in by
  // passing currentUserId (the per-story/spec threads do not).
  const isNew = (c: Comment): boolean =>
    currentUserId !== undefined &&
    c.authorUserId !== currentUserId &&
    (newSince === null ||
      newSince === undefined ||
      new Date(c.createdAt).getTime() > new Date(newSince).getTime());

  const submit = async (): Promise<void> => {
    if (!body.trim() && !file) return;
    const attachment = file
      ? {
          filename: file.name,
          contentType: file.type || 'application/octet-stream',
          dataBase64: await fileToBase64(file),
        }
      : undefined;
    onSubmit({ body: body.trim(), internal, ...(attachment ? { attachment } : {}) });
    setBody('');
    setInternal(false);
    setFile(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div className="flex flex-col gap-3">
      {isLoading ? (
        <p className="text-sm opacity-60">Loading…</p>
      ) : isError ? (
        <Alert>Could not load messages.</Alert>
      ) : !comments || comments.length === 0 ? (
        <p className="text-sm opacity-60">{emptyText}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {(ordered ?? []).map((c) => {
            const fresh = isNew(c);
            return (
              <li
                key={c.id}
                className={`rounded-md p-3 text-sm ${
                  fresh
                    ? 'bg-primary/5 ring-1 ring-primary/40'
                    : c.visibility === 'internal'
                      ? 'bg-amber-50 ring-1 ring-amber-200'
                      : 'bg-neutral/50'
                }`}
              >
                <div className="mb-1 flex flex-wrap items-center gap-2 text-xs opacity-70">
                  {fresh ? (
                    <span className="rounded-full bg-primary px-1.5 py-0.5 font-semibold uppercase tracking-wide text-white opacity-100">
                      New
                    </span>
                  ) : null}
                  <span className="font-medium">{c.authorEmail}</span>
                  {showSubject ? (
                    c.subjectType === 'user_story' && storyHref ? (
                      <Link
                        to={storyHref(c.subjectId)}
                        className="rounded bg-white px-1.5 py-0.5 text-primary underline ring-1 ring-secondary/10 hover:bg-neutral"
                        title="Go to this story"
                      >
                        {c.subjectLabel} ↗
                      </Link>
                    ) : (
                      <span className="rounded bg-white px-1.5 py-0.5 ring-1 ring-secondary/10">
                        {c.subjectLabel}
                      </span>
                    )
                  ) : null}
                  {c.visibility === 'internal' ? (
                    <span className="font-medium text-amber-800">internal</span>
                  ) : null}
                  <span className="ml-auto">{new Date(c.createdAt).toLocaleString()}</span>
                </div>
                {c.body ? <p className="whitespace-pre-wrap">{c.body}</p> : null}
                {c.attachment ? (
                  <button
                    className="mt-1 inline-flex items-center gap-1 text-primary underline"
                    onClick={() => void downloadAttachment(projectId, c.attachment!)}
                  >
                    📎 {c.attachment.filename}
                    <span className="opacity-60">
                      ({Math.max(1, Math.round(c.attachment.size / 1024))} KB)
                    </span>
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex flex-col gap-2">
        <textarea
          className="min-h-16 rounded-md border border-secondary/20 bg-white px-3 py-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          placeholder={placeholder}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        {error ? <Alert>{error}</Alert> : null}
        <div className="flex flex-wrap items-center gap-3">
          <Button disabled={isSubmitting || (!body.trim() && !file)} onClick={() => void submit()}>
            {isSubmitting ? 'Sending…' : 'Send'}
          </Button>
          {/* The native file input is hidden; a styled button stands in for it so
              it reads as a control, not stray text. */}
          <input ref={fileRef} type="file" onChange={onFile} className="sr-only" />
          <Button variant="secondary" onClick={() => fileRef.current?.click()}>
            Choose file
          </Button>
          {file ? (
            <span className="inline-flex items-center gap-1 text-sm">
              <span className="max-w-[12rem] truncate" title={file.name}>
                {file.name}
              </span>
              <button
                type="button"
                className="rounded px-1 text-secondary/70 hover:bg-neutral hover:text-secondary"
                aria-label="Remove file"
                onClick={clearFile}
              >
                ✕
              </button>
            </span>
          ) : (
            <span className="text-sm opacity-60">No file chosen</span>
          )}
          {canMarkInternal ? (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={internal}
                onChange={(e) => setInternal(e.target.checked)}
              />
              Internal (developers only)
            </label>
          ) : null}
        </div>
      </div>
    </div>
  );
}
